const getPrompt = (text: string, targetLanguage: string) => `
You are a professional translator. Translate the following text into ${targetLanguage}.
Respond ONLY with the translated text, nothing else. No markdown, no explanations.
Text to translate:
"${text}"
`;

const getObjectPrompt = (obj: any, targetLanguage: string) => `
You are a professional translator. Translate the string values of the following JSON object into ${targetLanguage}.
Respond ONLY with a valid JSON object matching the exact keys of the input. No markdown fences, no explanations.
JSON to translate:
${JSON.stringify(obj)}
`;

const getSrtPrompt = (srtText: string, targetLanguage: string) => `
You are a professional subtitle translator. Translate the following SRT file content into ${targetLanguage}.
CRITICAL INSTRUCTIONS:
1. Preserve ALL SRT formatting exactly (sequence numbers, timestamp lines like '00:00:01,000 --> 00:00:04,000').
2. Only translate the dialogue text.
3. Respond ONLY with the valid SRT text. No markdown fences, no explanations.

SRT Content:
${srtText}
`;

export const translateWithGemini = async (prompt: string, apiKey: string) => {
  const url = `https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-flash:generateContent?key=${apiKey}`;
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      contents: [{ parts: [{ text: prompt }] }]
    })
  });
  if (!response.ok) throw new Error('Gemini API request failed');
  const data = await response.json();
  let result = data.candidates?.[0]?.content?.parts?.[0]?.text?.trim() || '';
  // Remove possible markdown JSON formatting for object translation
  if (result.startsWith('\`\`\`json')) result = result.replace(/^\`\`\`json\n|\n\`\`\`$/g, '');
  if (result.startsWith('\`\`\`')) result = result.replace(/^\`\`\`\n|\n\`\`\`$/g, '');
  return result;
};

export const translateWithDeepSeek = async (prompt: string, apiKey: string) => {
  const url = 'https://api.deepseek.com/chat/completions';
  const response = await fetch(url, {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`
    },
    body: JSON.stringify({
      model: "deepseek-chat",
      messages: [{ role: "user", content: prompt }]
    })
  });
  if (!response.ok) throw new Error('DeepSeek API request failed');
  const data = await response.json();
  let result = data.choices?.[0]?.message?.content?.trim() || '';
  if (result.startsWith('\`\`\`json')) result = result.replace(/^\`\`\`json\n|\n\`\`\`$/g, '');
  if (result.startsWith('\`\`\`')) result = result.replace(/^\`\`\`\n|\n\`\`\`$/g, '');
  return result;
};

export const translateWithNvidia = async (prompt: string, apiKey: string) => {
  const url = 'https://integrate.api.nvidia.com/v1/chat/completions';
  const response = await fetch(url, {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`
    },
    body: JSON.stringify({
      model: "meta/llama-3.3-70b-instruct",
      messages: [{ role: "user", content: prompt }]
    })
  });
  if (!response.ok) throw new Error('NVIDIA API request failed');
  const data = await response.json();
  let result = data.choices?.[0]?.message?.content?.trim() || '';
  if (result.startsWith('\`\`\`json')) result = result.replace(/^\`\`\`json\n|\n\`\`\`$/g, '');
  if (result.startsWith('\`\`\`')) result = result.replace(/^\`\`\`\n|\n\`\`\`$/g, '');
  return result;
};

export const translateWithGroq = async (prompt: string, apiKey: string) => {
  const url = 'https://api.groq.com/openai/v1/chat/completions';
  const response = await fetch(url, {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`
    },
    body: JSON.stringify({
      model: "llama-3.1-8b-instant",
      messages: [{ role: "user", content: prompt }]
    })
  });
  if (!response.ok) throw new Error('Groq API request failed');
  const data = await response.json();
  let result = data.choices?.[0]?.message?.content?.trim() || '';
  if (result.startsWith('\`\`\`json')) result = result.replace(/^\`\`\`json\n|\n\`\`\`$/g, '');
  if (result.startsWith('\`\`\`')) result = result.replace(/^\`\`\`\n|\n\`\`\`$/g, '');
  return result;
};

export const translateWithGithub = async (prompt: string, apiKey: string) => {
  const url = 'https://models.inference.ai.azure.com/chat/completions';
  const response = await fetch(url, {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`
    },
    body: JSON.stringify({
      model: "gpt-4o",
      messages: [{ role: "user", content: prompt }]
    })
  });
  if (!response.ok) throw new Error('GitHub API request failed');
  const data = await response.json();
  let result = data.choices?.[0]?.message?.content?.trim() || '';
  if (result.startsWith('\`\`\`json')) result = result.replace(/^\`\`\`json\n|\n\`\`\`$/g, '');
  if (result.startsWith('\`\`\`')) result = result.replace(/^\`\`\`\n|\n\`\`\`$/g, '');
  return result;
};

const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

const executeTranslation = async (prompt: string): Promise<string> => {
  await delay(500);

  const geminiKeyStr = localStorage.getItem('GEMINI_API_KEY');
  const deepseekKey = localStorage.getItem('DEEPSEEK_API_KEY');
  const nvidiaKey = localStorage.getItem('NVIDIA_NIM_API_KEY');
  const groqKey = localStorage.getItem('GROQ_API_KEY');
  const githubKey = localStorage.getItem('GITHUB_API_KEY');

  if (geminiKeyStr) {
    const keys = geminiKeyStr.split(',').map(k => k.trim()).filter(k => k);
    for (const key of keys) {
      try {
        return await translateWithGemini(prompt, key);
      } catch (e) {
        console.warn('Gemini key failed, trying next...', e);
      }
    }
  }

  if (groqKey) {
    try { return await translateWithGroq(prompt, groqKey); } catch (e) { console.warn(e); }
  }
  
  if (githubKey) {
    try { return await translateWithGithub(prompt, githubKey); } catch (e) { console.warn(e); }
  }

  if (deepseekKey) {
    try { return await translateWithDeepSeek(prompt, deepseekKey); } catch (e) { console.warn(e); }
  }

  if (nvidiaKey) {
    try { return await translateWithNvidia(prompt, nvidiaKey); } catch (e) { console.warn(e); }
  }

  throw new Error('All configured AI API keys failed or none are configured. Please check your Settings.');
};

export const autoTranslate = async (text: string, targetLanguage: string): Promise<string> => {
  if (!text) return '';
  return executeTranslation(getPrompt(text, targetLanguage));
};

export const autoTranslateObject = async (obj: any, targetLanguage: string): Promise<any> => {
  if (!obj || Object.keys(obj).length === 0) return obj;
  let resStr = await executeTranslation(getObjectPrompt(obj, targetLanguage));
  try {
    // Strip markdown formatting if present
    resStr = resStr.trim();
    if (resStr.startsWith('```')) {
      const lines = resStr.split('\n');
      if (lines[0].startsWith('```')) lines.shift();
      if (lines[lines.length - 1].startsWith('```')) lines.pop();
      resStr = lines.join('\n').trim();
    }
    return JSON.parse(resStr);
  } catch (e) {
    console.error("Failed to parse AI JSON response", resStr);
    return obj;
  }
};

export const translateSRT = async (srtText: string, targetLanguage: string): Promise<string> => {
  if (!srtText) return '';
  let resStr = await executeTranslation(getSrtPrompt(srtText, targetLanguage));
  resStr = resStr.trim();
  if (resStr.startsWith('```')) {
    const lines = resStr.split('\n');
    if (lines[0].startsWith('```')) lines.shift();
    if (lines[lines.length - 1].startsWith('```')) lines.pop();
    resStr = lines.join('\n').trim();
  }
  return resStr;
};

const getTaggingPrompt = (synopsis: string) => `
You are an expert content manager. Analyze the following story synopsis and suggest the best primary Category and up to 5 Genres/Keywords.
Respond ONLY with a valid JSON object matching this exact format: {"category": "String", "genres": ["String", "String"]}.
No markdown fences, no explanations.

Synopsis:
"${synopsis}"
`;

export const autoTagStory = async (synopsis: string): Promise<{category: string, genres: string[]}> => {
  if (!synopsis) return { category: '', genres: [] };
  let resStr = await executeTranslation(getTaggingPrompt(synopsis));
  try {
    resStr = resStr.trim();
    if (resStr.startsWith('```')) {
      const lines = resStr.split('\n');
      if (lines[0].startsWith('```')) lines.shift();
      if (lines[lines.length - 1].startsWith('```')) lines.pop();
      resStr = lines.join('\n').trim();
    }
    return JSON.parse(resStr);
  } catch (e) {
    console.error("Failed to parse AI tags response", resStr);
    return { category: '', genres: [] };
  }
};

const getRewritePrompt = (synopsis: string, targetLanguage: string) => `
You are a master storyteller, professional copywriter, and an expert in Arabic literature and folklore.
Please rewrite the following synopsis to make it highly engaging, captivating, and emotionally resonant.
Use elegant, poetic, and structurally sound Arabic phrasing (or the requested target language).
Avoid literal translations, hallucinations, or adding facts not present in the original text. Keep the core story intact but elevate the prose significantly.
Respond ONLY with the rewritten text in ${targetLanguage}, nothing else. No markdown, no explanations.

Original Synopsis:
"${synopsis}"
`;

export const autoRewriteSynopsis = async (synopsis: string, targetLanguage: string): Promise<string> => {
  if (!synopsis) return '';
  return executeTranslation(getRewritePrompt(synopsis, targetLanguage));
};

